import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// NOTE: import each primitive directly (not via './ui' barrel) — AppShell
// lives in that barrel and imports this file back, creating a circular
// init-order chain ("Cannot access 'AppShell' before initialization").
import { Drawer } from './ui/feedback/Drawer';
import { Button } from './ui/primitives/Button';
import { Icon } from './ui/primitives/Icon';
import { EmptyState } from './ui/layout/EmptyState';
import { formatDate } from './ui/tokens';
import { API_URL } from '../config/api';
import { useToast } from '../contexts/ToastContext';

interface Notification {
  id: string;
  title?: string | null;
  message: string;
  type: string;
  category?: string | null;
  priority?: string | null;
  linkUrl?: string | null;
  isRead: boolean;
  relatedEntityId?: string | null;
  relatedEntityType?: string | null;
  createdAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

// Category-driven icon/color. deal_signed (high priority) gets a red/amber
// accent so signed-not-won deals stand out.
const CATEGORY_META: Record<string, { icon: string; color: string }> = {
  mail:        { icon: 'mail',          color: 'bg-sky-50 text-sky-600' },
  deal_signed: { icon: 'edit_document', color: 'bg-amber-50 text-amber-700' },
  deal_won:    { icon: 'emoji_events',  color: 'bg-emerald-50 text-emerald-600' },
  meeting:     { icon: 'event',         color: 'bg-purple-50 text-purple-600' },
  quote:       { icon: 'description',   color: 'bg-indigo-50 text-indigo-600' },
  system:      { icon: 'settings',      color: 'bg-slate-100 text-slate-600' },
  general:     { icon: 'info',          color: 'bg-blue-50 text-blue-600' },
};

// Fallback to the legacy `type` field for older rows without a category.
const TYPE_META: Record<string, { icon: string; color: string }> = {
  info:    { icon: 'info',           color: 'bg-blue-50 text-blue-600' },
  success: { icon: 'check_circle',   color: 'bg-emerald-50 text-emerald-600' },
  warning: { icon: 'warning',        color: 'bg-amber-50 text-amber-700' },
  error:   { icon: 'error',          color: 'bg-red-50 text-red-600' },
  quote:   { icon: 'description',    color: 'bg-indigo-50 text-indigo-600' },
  deal:    { icon: 'work',           color: 'bg-purple-50 text-purple-600' },
  task:    { icon: 'task_alt',       color: 'bg-slate-100 text-slate-600' },
};

const metaFor = (n: Notification) =>
  (n.category && CATEGORY_META[n.category]) || TYPE_META[n.type] || CATEGORY_META.general;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const entityLink = (n: Notification): string | null => {
  // Prefer the explicit linkUrl; fall back to the legacy entity routing.
  if (n.linkUrl) return n.linkUrl;
  if (!n.relatedEntityId || !n.relatedEntityType) return null;
  switch (n.relatedEntityType) {
    case 'quote': return `/quotes/${n.relatedEntityId}/edit`;
    case 'deal':  return `/deals/${n.relatedEntityId}`;
    case 'customer': return `/customers/${n.relatedEntityId}`;
    case 'company':  return `/companies/${n.relatedEntityId}`;
    default: return null;
  }
};

export const NotificationsDrawer: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/notifications`, { headers: authHeaders() });
      const rows: Notification[] = Array.isArray(r.data) ? r.data : [];
      // Unread first, then newest first.
      rows.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setItems(rows);
    } catch {
      // silent — pop-in drawer shouldn't scream
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const markRead = async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers: authHeaders() });
      onChanged?.();
    } catch {
      toast.error('שגיאה בסימון כנקרא');
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: authHeaders() });
      toast.success('כל ההתראות סומנו כנקראות');
      onChanged?.();
    } catch {
      toast.error('שגיאה');
    }
  };

  const onItemClick = (n: Notification) => {
    if (!n.isRead) markRead(n.id);
    const link = entityLink(n);
    if (link) {
      onClose();
      navigate(link);
    }
  };

  const unreadCount = items.filter((n) => !n.isRead).length;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="התראות"
      subtitle={unreadCount > 0 ? `${unreadCount} חדשות` : 'הכל מעודכן'}
      size="md"
      footer={
        unreadCount > 0 ? (
          <Button variant="secondary" icon="done_all" onClick={markAllRead} fullWidth>
            סמן הכל כנקרא
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <div className="py-10 text-center text-sm text-slate-500">
          <Icon name="progress_activity" size={22} className="animate-spin mx-auto mb-2" />
          טוען התראות...
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="notifications_off"
          title="אין התראות חדשות"
          description="הכל שקט. התראות חדשות יופיעו כאן."
        />
      ) : (
        <div className="space-y-1.5">
          {items.map((n) => {
            const meta = metaFor(n);
            const clickable = !!entityLink(n);
            const isHigh = n.priority === 'high';
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => onItemClick(n)}
                className={[
                  'w-full text-start rounded-xl p-3 flex items-start gap-3 transition-colors border',
                  !n.isRead ? 'bg-primary/5' : 'bg-white',
                  clickable ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default',
                  isHigh && !n.isRead ? 'border-amber-300' : 'border-slate-100',
                ].join(' ')}
              >
                <div className={`size-9 rounded-lg ${meta.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={meta.icon} size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  {n.title && (
                    <div className={['text-sm leading-snug', n.isRead ? 'text-slate-700' : 'text-slate-900 font-semibold'].join(' ')}>
                      {n.title}
                    </div>
                  )}
                  <div className={[
                    n.title ? 'text-[12px] text-slate-600' : 'text-sm leading-snug',
                    !n.title && (n.isRead ? 'text-slate-700' : 'text-slate-900 font-semibold'),
                  ].filter(Boolean).join(' ')}>
                    {n.message}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {formatDate(n.createdAt)}
                  </div>
                </div>
                {!n.isRead && (
                  <div className={['size-2 rounded-full flex-shrink-0 mt-1.5', isHigh ? 'bg-red-500' : 'bg-primary'].join(' ')} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};
